/**
 * One animal, in five tabs (P2-TR3).
 *
 * Overview · Weights · Health · Breeding · Costs. Tabs rather than one long
 * page because the questions are separate: "is she pregnant?" and "what has
 * this goat cost us?" are asked on different days, and a partner scrolling
 * past four sections to reach the fifth is a partner who stops opening the
 * screen.
 *
 * Costs is last and deliberately complete. BR-13 says an animal's cost is its
 * price plus its share of the trip plus its medicine plus its feed, and the
 * whole point is that a partner can see all four in one place — a per-animal
 * profit figure nobody can decompose is a figure nobody believes.
 */

import { Suspense, lazy, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ProtectedImage } from '../../components/ProtectedImage.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { useAuth } from '../auth/AuthContext.js';
import { AnimalSaleForm } from './AnimalSaleForm.js';
import { formatMoney } from '../../lib/currency.js';
import { daysBetween, formatDate, todayInDhaka } from '../../lib/date.js';
import { growthVerdict } from './growth.js';
import { ageMonthsFromDays } from './animalAge.js';
import { useAnimalProfile, useGrowthSeries } from './api.js';

const GrowthChart = lazy(() => import('./GrowthChart.js'));

const TABS = ['overview', 'weights', 'health', 'breeding', 'costs'] as const;
type Tab = (typeof TABS)[number];

const VERDICT_STYLE: Record<string, string> = {
  good: 'bg-green-50 text-green-800',
  warn: 'bg-amber-50 text-amber-900',
  neutral: 'bg-gray-100 text-gray-600',
};

export function AnimalProfile(): JSX.Element {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const animalId = Number(params.id);

  const [tab, setTab] = useState<Tab>('overview');
  const { user } = useAuth();

  // Selling is income, which §1.4 makes a partner's job. The server enforces
  // the same on POST /sales/animal.
  const canSell = user?.role === 'admin' || user?.role === 'partner';
  const { data: animal, isLoading, isError, refetch } = useAnimalProfile(animalId);
  // Only fetched when the tab is open: the chart data is the largest payload
  // on this screen and most visits never look at it (NFR-3).
  const { data: growth } = useGrowthSeries(animalId);

  if (isLoading) {
    return (
      <p role="status" className="py-8 text-center text-gray-500">
        {t('app.loading')}
      </p>
    );
  }

  if (isError || !animal) {
    return (
      <div role="alert" className="mx-auto max-w-md px-4 py-8 text-sm text-red-800">
        <p className="mb-2">{t('error.network')}</p>
        <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
          {t('app.retry')}
        </button>
      </div>
    );
  }

  const verdict = growth ? growthVerdict(growth) : null;

  return (
    <div className="page-container">
      <header className="flex items-center gap-3 px-4 py-4">
        {animal.photoUrl !== null ? (
          <ProtectedImage
            uploadId={animal.photoUrl}
            alt={animal.tag}
            width={64}
            height={64}
            className="h-16 w-16 rounded-xl object-cover"
          />
        ) : (
          <div
            className="flex h-16 w-16 items-center justify-center rounded-xl bg-gray-100 text-2xl"
            aria-hidden="true"
          >
            {animal.species === 'cattle' ? '🐄' : '🐐'}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold text-gray-900">{animal.tag}</h1>
          <p className="truncate text-sm text-gray-500">
            {t(`animals.species.${animal.species}`)}
            {animal.breed !== null && ` · ${animal.breed}`}
            {animal.latestWeightKg !== null && ` · ${animal.latestWeightKg} kg`}
          </p>
        </div>

        {/* P4-T34 — the same people who register an animal may correct it. */}
        {canSell && (
          <Link
            to={`/animals/${String(animal.id)}/edit`}
            className="min-h-touch shrink-0 rounded-lg bg-white px-4 py-2 text-sm font-medium text-green-800 shadow-sm"
          >
            {t('animals.edit.open')}
          </Link>
        )}
      </header>

      <div className="sticky top-0 z-10 overflow-x-auto border-b border-gray-200 bg-white" role="tablist">
        <div className="flex min-w-max">
          {TABS.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={tab === option}
              onClick={() => setTab(option)}
              className={`min-h-touch px-4 py-3 text-sm ${
                tab === option ? 'border-b-2 border-green-700 font-medium text-green-800' : 'text-gray-600'
              }`}
            >
              {t(`animals.tab.${option}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-4">
        {tab === 'overview' && (
          <dl className="space-y-3 rounded-xl bg-white p-4 shadow-sm">
            {[
              ['animals.field.status', t(`animals.status.${animal.status}`)],
              ['animals.field.sex', t(`animals.sex.${animal.sex}`)],
              ['animals.field.source', t(`animals.source.${animal.source}`)],
              [
                'animals.field.age',
                (() => {
                  const months = ageMonthsFromDays(animal.ageDays);
                  return months !== null ? t('animals.ageMonths', { count: months }) : '—';
                })(),
              ],
              ['animals.field.purchaseDate', animal.purchaseDate !== null ? formatDate(animal.purchaseDate) : '—'],
            ].map(([key, value]) => (
              <div key={key} className="flex justify-between gap-3 text-sm">
                <dt className="text-gray-500">{t(key!)}</dt>
                <dd className="text-gray-900">{value}</dd>
              </div>
            ))}
          </dl>
        )}

        {tab === 'weights' && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            {verdict && (
              <p className={`mb-3 rounded-lg px-3 py-2 text-sm ${VERDICT_STYLE[verdict.tone]}`}>
                {t(verdict.key)}
                {verdict.averageDailyGainGrams !== null &&
                  ` — ${t('growth.perDay', { grams: verdict.averageDailyGainGrams })}`}
              </p>
            )}

            {growth && (
              <Suspense fallback={<p className="py-8 text-center text-sm text-gray-400">{t('app.loading')}</p>}>
                <GrowthChart series={growth} />
              </Suspense>
            )}

            {/* Readable without the chart, so a slow connection never blocks
                the actual numbers. */}
            <ul className="mt-3 divide-y divide-gray-100">
              {animal.weights.map((weight) => (
                <li key={weight.date} className="flex justify-between py-2 text-sm">
                  <span className="text-gray-600">{formatDate(weight.date)}</span>
                  <span className="tabular-nums text-gray-900">{weight.weightKg} kg</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tab === 'health' && (
          <ul className="space-y-2">
            {animal.healthEvents.length === 0 && (
              <p className="py-8 text-center text-gray-500">{t('animals.health.empty')}</p>
            )}
            {animal.healthEvents.map((event) => (
              <li key={event.id} className="rounded-xl bg-white p-3 shadow-sm">
                <div className="flex justify-between gap-2">
                  <span className="font-medium text-gray-800">{t(`health.type.${event.eventType}`)}</span>
                  <span className="text-xs text-gray-500">{formatDate(event.date)}</span>
                </div>
                {event.detail !== null && <p className="mt-1 text-sm text-gray-600">{event.detail}</p>}
                {event.costAmount !== null && (
                  <p className="mt-1 text-sm tabular-nums text-gray-800">{formatMoney(event.costAmount)}</p>
                )}
                {event.nextDue !== null && (
                  <p className="mt-1 text-xs text-amber-800">
                    {t('health.nextDue', { date: formatDate(event.nextDue) })}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}

        {tab === 'breeding' && (
          <ul className="space-y-2">
            {animal.breedingRecords.length === 0 && (
              <p className="py-8 text-center text-gray-500">{t('animals.breeding.empty')}</p>
            )}
            {animal.breedingRecords.map((record) => (
              <li key={record.id} className="rounded-xl bg-white p-3 shadow-sm">
                <div className="flex justify-between gap-2">
                  <span className="font-medium text-gray-800">{t(`breeding.method.${record.method}`)}</span>
                  <span className="text-xs text-gray-500">{formatDate(record.date)}</span>
                </div>
                {record.expectedDelivery !== null && record.actualDelivery === null && (
                  <p className="mt-1 text-sm text-gray-700">
                    {t('breeding.expected', { date: formatDate(record.expectedDelivery) })}
                    {daysBetween(todayInDhaka(), record.expectedDelivery) >= 0 &&
                      ` · ${t('breeding.inDays', { count: daysBetween(todayInDhaka(), record.expectedDelivery) })}`}
                  </p>
                )}
                {record.actualDelivery !== null && (
                  <p className="mt-1 text-sm text-green-800">
                    {t('breeding.delivered', {
                      date: formatDate(record.actualDelivery),
                      count: record.offspringCount ?? 0,
                    })}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}

        {tab === 'costs' && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <dl className="space-y-2">
              {[
                ['animals.cost.purchase', animal.cost.purchasePrice],
                ['animals.cost.trip', animal.cost.tripCostAllocated],
                ['animals.cost.health', animal.cost.medicalCost],
              ].map(([key, value]) => (
                <div key={key} className="flex justify-between gap-3 text-sm">
                  <dt className="text-gray-500">{t(key!)}</dt>
                  <dd className="tabular-nums text-gray-900">{formatMoney(value!)}</dd>
                </div>
              ))}
            </dl>

            {/* The four lines above add to this one, on screen, because a total
                a partner cannot decompose is a total nobody believes (BR-13). */}
            <div className="mt-3 flex justify-between border-t border-gray-200 pt-3 font-medium">
              <span>{t('animals.cost.total')}</span>
              <span className="tabular-nums">{formatMoney(animal.cost.totalCost)}</span>
            </div>
            {/* Said rather than left out, so nobody reads the total as the whole of BR-13. */}
            {!animal.cost.feedIncluded && (
              <p className="mt-2 text-xs text-gray-500">{t('animals.cost.feedNotIncluded')}</p>
            )}

            {/* Selling lives here rather than on the overview: the figure that
                should be in front of a partner setting a price is what the
                animal has already cost (BR-16). Only an animal still in the
                herd can be sold, and the server refuses the rest regardless. */}
            {canSell && animal.status === 'active' && (
              <AnimalSaleForm animalId={animal.id} tag={animal.tag} totalCost={animal.cost.totalCost} />
            )}

            {animal.status === 'sold' && (
              <p className="mt-4 rounded-lg bg-gray-100 px-4 py-3 text-sm text-gray-600">{t('animals.sale.already')}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
